import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { ApiError, isUnreachable, post } from './api.js';
import { t } from '../i18n/index.js';
import { hasNativePasskey, isNativeApp, nativePasskeyAssertion } from './native.js';

import { AppTooOldError, markCeremony, signInWasInterrupted } from './passkey-session.js';

// Re-exported so every screen keeps one import for the sign-in vocabulary.
export { AppTooOldError, signInWasInterrupted };

type Ceremony<O> = { challengeId: string; options: O };

/** Two round trips: options (server stores the challenge) → browser prompt → verify (challenge consumed). */
export async function registerPasskey<T>(
  optionsUrl: string,
  verifyUrl: string,
  extraBody: Record<string, unknown> = {},
): Promise<T> {
  if (!browserSupportsWebAuthn()) throw new Error(t('passkey_unsupported'));
  const { challengeId, options } = await post<Ceremony<PublicKeyCredentialCreationOptionsJSON>>(
    optionsUrl,
    extraBody,
  );
  const response = await startRegistration({ optionsJSON: options });
  return post<T>(verifyUrl, { ...extraBody, challengeId, response });
}

/**
 * Get an assertion for a challenge the caller already has.
 *
 * Inside the app, ask Android directly first. A WebView only offers passkeys when the version of
 * Android System WebView on that phone supports it — a dependency on something nobody here
 * controls, and the reason two or three employees met a prompt that never appeared. Credential
 * Manager is what Chrome asks, and Chrome worked on the same phones with the same passkey.
 *
 * This exists as its own function because the punch cannot use authenticatePasskey: it sends the
 * assertion with a location, an integrity verdict, an app version and sometimes a reason for
 * leaving early. It called startAuthentication directly instead, which meant sign-in went native
 * and the punch — the thing an employee does twice a day — did not.
 */
export async function passkeyAssertion(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<unknown> {
  markCeremony(true);
  try {
    const native = await nativePasskeyAssertion(JSON.stringify(options));
    if (native.kind === 'ok') return native.response;

    if (!browserSupportsWebAuthn()) throw new Error(t('passkey_unsupported'));
    try {
      return await startAuthentication({ optionsJSON: options });
    } catch (err) {
      // The WebView route is the one that fails on the phones this keeps happening to. Say which
      // of the two things went wrong rather than reporting the symptom of the second.
      if (native.kind === 'failed') throw new Error(native.message, { cause: err });
      if (isNativeApp() && !hasNativePasskey() && isPasskeyCancelled(err)) throw new AppTooOldError();
      throw err;
    }
  } finally {
    markCeremony(false);
  }
}

export async function authenticatePasskey<T>(optionsUrl: string, verifyUrl: string): Promise<T> {
  const { challengeId, options } =
    await post<Ceremony<PublicKeyCredentialRequestOptionsJSON>>(optionsUrl);
  // The ceremony is the same one the punch performs; only what is sent with the answer differs.
  const response = await passkeyAssertion(options);
  return post<T>(verifyUrl, { challengeId, response });
}

/**
 * Cancel a prompt that is still open. Windows keeps its dialog up after the page moves on, and a
 * second ceremony then fails outright, so every screen that starts one cancels it on unmount.
 */
export function cancelPasskeyCeremony(): void {
  try {
    WebAuthnAbortService.cancelCeremony();
  } catch {
    /* nothing was in flight */
  }
}

/**
 * True when the person dismissed the prompt, the ceremony was aborted, or this device holds no
 * matching passkey. The platform reports all three as NotAllowedError, so they share one
 * treatment: a quiet hint, never an error banner and never an automatic retry.
 */
export function isPasskeyCancelled(err: unknown): boolean {
  const e = err as { name?: string; code?: string; cause?: { name?: string } };
  return (
    e?.name === 'NotAllowedError' ||
    e?.name === 'AbortError' ||
    e?.cause?.name === 'NotAllowedError' ||
    e?.cause?.name === 'AbortError' ||
    e?.code === 'ERROR_CEREMONY_ABORTED'
  );
}

/** Human message for any error thrown by the helpers above. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  const e = err as { name?: string; message?: string };
  if (e?.name === 'NotAllowedError' || e?.name === 'AbortError') return t('passkey_cancelled');
  if (e?.name === 'InvalidStateError') return t('passkey_cancelled');
  if (isUnreachable(err))
    return navigator.onLine ? t('api_unreachable') : t('offline_generic');
  return e?.message || t('error_generic');
}
